import { useEffect, useLayoutEffect, useRef, useState } from 'react'

const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)'
const STAGGER_MS = 45
const STAGGER_CAP = 11
// Items past this are off-screen on any normal viewport; animating them only
// costs main-thread time on long lists, so they simply appear.
const ENTRANCE_CAP = 24
// Long enough for the capped stagger to finish.
const SETTLE_MS = 800

function prefersReducedMotion() {
  return typeof window !== 'undefined' && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches)
}

function measure(container) {
  const positions = new Map()
  for (const el of container.children) {
    if (el.dataset.flipId) positions.set(el.dataset.flipId, { x: el.offsetLeft, y: el.offsetTop })
  }
  return positions
}

/**
 * A card grid whose cards rise in with a stagger when it mounts, and glide to
 * their new place (FLIP) when sorting or filtering reorders them. Cards that
 * arrive later, like a search widening the results, rise in without waiting
 * on the stagger. Remount it (change its `key`) to replay the full entrance.
 *
 * Both motions run through element.animate(), not CSS classes: React reorders
 * keyed children by re-inserting their nodes, and re-insertion restarts CSS
 * animations, so a class-based entrance would replay on every card that moved.
 */
// `enterFrom` is the entrance's starting transform; full-width list rows pass
// a plain rise, since scaling a wide row reads as a wobble.
export default function CollectionGrid({ items, getKey, className = '', busy = false, enterFrom = 'translateY(14px) scale(0.97)', children }) {
  const ref = useRef(null)
  const positions = useRef(new Map())
  const settledRef = useRef(false)
  const [settled, setSettled] = useState(false)
  const order = items.map(getKey).join('|')

  useEffect(() => {
    const timer = setTimeout(() => { settledRef.current = true; setSettled(true) }, SETTLE_MS)
    return () => clearTimeout(timer)
  }, [])

  // offsetLeft/offsetTop are layout positions, so a move that starts while an
  // earlier one is still running measures from where the card really sits.
  useLayoutEffect(() => {
    const container = ref.current
    if (!container) return
    const next = measure(container)
    if (!prefersReducedMotion()) {
      let index = 0
      for (const el of container.children) {
        const id = el.dataset.flipId
        const prev = positions.current.get(id)
        const now = next.get(id)
        if (!el.animate || !now) continue
        if (!prev) {
          if (index < ENTRANCE_CAP) el.animate(
            [
              { opacity: 0, transform: enterFrom },
              { opacity: 1, transform: 'none' },
            ],
            {
              duration: 560,
              easing: EASE,
              delay: settledRef.current ? 0 : Math.min(index, STAGGER_CAP) * STAGGER_MS,
              fill: 'backwards',
            },
          )
        } else if (prev.x !== now.x || prev.y !== now.y) {
          el.animate(
            [{ transform: `translate(${prev.x - now.x}px, ${prev.y - now.y}px)` }, { transform: 'none' }],
            { duration: 480, easing: EASE },
          )
        }
        index += 1
      }
    }
    positions.current = next
  }, [order, enterFrom])

  // Column count changes with the container width; keep the baseline current
  // so the next reorder doesn't animate from a stale layout.
  useEffect(() => {
    const container = ref.current
    if (!container || typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(() => { positions.current = measure(container) })
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      aria-busy={busy || undefined}
      className={`transition-opacity duration-300 ${busy ? 'pointer-events-none opacity-60' : ''} ${className}`}
    >
      {items.map((item, index) => {
        const key = getKey(item)
        // --i staggers anything inside that draws itself in (the confidence ring).
        return (
          <div key={key} data-flip-id={key} style={{ '--i': settled ? 0 : index }}>
            {children(item, index)}
          </div>
        )
      })}
    </div>
  )
}
